import { supabase } from '@/lib/supabase';
import { PLAN_INCLUDED_LEADS, SINGLE_LEAD_PRODUCT, planLabel, Lang } from '@/data/constants';

/**
 * ---------------------------------------------------------------------------
 * POST-PAYMENT ACTIVATION — single source of truth for the return journey.
 *
 * Required flow (and what enforces each step):
 *
 *   Payment approved
 *     -> hosted checkout redirects to /payment-complete            (checkout-link adds the redirect)
 *     -> signed webhook writes mkt_payment_events                  (payment-webhook, verified secret)
 *     -> plan activated + credits assigned SERVER-SIDE             (mkt_claim_payment_activation)
 *     -> customer lands on Projects with the balance visible       (PaymentComplete -> /jobs)
 *
 * CREDITS ARE NEVER GRANTED BY THE BROWSER REACHING THIS PAGE. claimActivation()
 * calls a SECURITY DEFINER database function that refuses unless a verified
 * webhook event already exists for the caller's OWN profile email. If the
 * webhook has not landed yet the page waits and says "Payment received. We are
 * activating your credits…".
 *
 * Credits per product — NEVER retyped here. Read from PLAN_INCLUDED_LEADS and
 * SINGLE_LEAD_PRODUCT in src/data/constants.ts:
 *   Basic $99 -> 5 · Pro $199 -> 10 · Premium $499 -> 25 · Single lead $29 -> 1
 * (Premium moved 20 -> 25; the $499 monthly charge did not change.)
 * ---------------------------------------------------------------------------
 */

export type ActivationState =
  | 'unauthenticated'
  | 'awaiting_webhook'
  | 'activated'
  | 'amount_mismatch'
  | 'not_yours'
  | 'no_profile'
  | 'error';

export interface ActivationResult {
  state: ActivationState;
  plan: string;
  /** Allowance the plan grants for the cycle. */
  included: number;
  /** Spendable right now (cycle remainder + $29 extras). */
  credits: number;
  extra: number;
  justActivated: boolean;
  alreadyActive: boolean;
}

const EMPTY: ActivationResult = {
  state: 'awaiting_webhook',
  plan: '',
  included: 0,
  credits: 0,
  extra: 0,
  justActivated: false,
  alreadyActive: false,
};

/**
 * Credits a product grants. Read from the shared catalog — memberships from
 * PLAN_INCLUDED_LEADS, the $29 one-off from SINGLE_LEAD_PRODUCT — so the
 * return page can label a single-lead purchase as honestly as a membership.
 */
export const creditsForPlan = (code: string): number =>
  code === SINGLE_LEAD_PRODUCT.code ? SINGLE_LEAD_PRODUCT.credits : PLAN_INCLUDED_LEADS[code] || 0;

/** True when the product code is the $29 one-off rather than a membership. */
export const isSingleLeadProduct = (code?: string | null): boolean =>
  code === SINGLE_LEAD_PRODUCT.code;


/**
 * Ask the database to attach the verified payment to this account and activate
 * the plan. Idempotent: calling it repeatedly (polling, refresh, second tab)
 * can only ever produce one subscription and one allowance.
 */
export const claimActivation = async (): Promise<ActivationResult> => {
  const { data, error } = await supabase.rpc('mkt_claim_payment_activation');
  if (error || !data) return { ...EMPTY, state: error ? 'error' : 'awaiting_webhook' };
  const row = data as Record<string, unknown>;
  return {
    state: (row.state as ActivationState) || 'awaiting_webhook',
    plan: String(row.plan || ''),
    included: Number(row.included || 0),
    credits: Number(row.credits || 0),
    extra: Number(row.extra || 0),
    justActivated: row.justActivated === true,
    alreadyActive: row.alreadyActive === true,
  };
};

export interface ReturnState {
  ok: boolean;
  hasAccount: boolean;
  webhookReceived: boolean;
  activated: boolean;
  plan: string;
  planCredits: number;
  credits: number;
}

/**
 * Signed-out lookup: has the webhook arrived for this email, and does the email
 * already have an account? Read-only — it grants nothing.
 */
export const lookupReturnState = async (email: string, plan?: string): Promise<ReturnState> => {
  try {
    const { data } = await supabase.functions.invoke('payment-return', { body: { email, plan } });
    return {
      ok: data?.ok === true,
      hasAccount: data?.hasAccount === true,
      webhookReceived: data?.webhookReceived === true,
      activated: data?.activated === true,
      plan: String(data?.plan || plan || ''),
      planCredits: Number(data?.planCredits || 0),
      credits: Number(data?.credits || 0),
    };
  } catch (_e) {
    return { ok: false, hasAccount: false, webhookReceived: false, activated: false, plan: plan || '', planCredits: 0, credits: 0 };
  }
};

// ---------------------------------------------------------------------------
// WELCOME TICKET
// Carries the "payment successful" confirmation from the return page to the
// Projects page (and survives a login / sign-up detour) so the customer is
// never dropped somewhere with no explanation of what just happened.
// ---------------------------------------------------------------------------

const TICKET_KEY = 'gg_purchase_welcome';
const TOUR_KEY = 'gg_tour_done';

export interface WelcomeTicket {
  plan: string;
  credits: number;
  /** 'plan' for a membership, 'lead' for the $29 single lead. */
  kind: 'plan' | 'lead';
  at: number;
}

export const setWelcomeTicket = (ticket: Omit<WelcomeTicket, 'at'>) => {
  try {
    localStorage.setItem(TICKET_KEY, JSON.stringify({ ...ticket, at: Date.now() }));
  } catch (_e) {
    /* private mode — the banner is a nicety, never a blocker */
  }
};

/** Read the ticket. Expires after 2 hours so it cannot haunt later visits. */
export const readWelcomeTicket = (): WelcomeTicket | null => {
  try {
    const raw = localStorage.getItem(TICKET_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as WelcomeTicket;
    if (!t || Date.now() - Number(t.at || 0) > 2 * 60 * 60 * 1000) {
      localStorage.removeItem(TICKET_KEY);
      return null;
    }
    return t;
  } catch (_e) {
    return null;
  }
};

export const clearWelcomeTicket = () => {
  try {
    localStorage.removeItem(TICKET_KEY);
  } catch (_e) {
    /* noop */
  }
};

// ---------------------------------------------------------------------------
// ONBOARDING TOUR STATE — shown once automatically after the first purchase,
// skippable, and replayable forever from the dashboard.
// ---------------------------------------------------------------------------

export const tourSeen = (): boolean => {
  try {
    return localStorage.getItem(TOUR_KEY) === '1';
  } catch (_e) {
    return true;
  }
};

export const markTourSeen = () => {
  try {
    localStorage.setItem(TOUR_KEY, '1');
  } catch (_e) {
    /* noop */
  }
};

export const resetTour = () => {
  try {
    localStorage.removeItem(TOUR_KEY);
  } catch (_e) {
    /* noop */
  }
};

/**
 * "Your Basic membership is active and 5 lead credits are ready."
 * Built from the shared plan catalog so the numbers can never drift.
 */
export const welcomeLine = (ticket: WelcomeTicket, lang: Lang): string => {
  const credits = ticket.credits;
  if (ticket.kind === 'lead') {
    return lang === 'es'
      ? `Pago exitoso. Recibió 1 crédito de lead y ya está listo para usarlo.`
      : `Payment successful. You received 1 lead credit and it is ready to use.`;
  }
  const name = planLabel(ticket.plan, lang);
  return lang === 'es'
    ? `Pago exitoso. Su membresía ${name} está activa y tiene ${credits} créditos de leads listos.`
    : `Payment successful. Your ${name} membership is active and ${credits} lead credits are ready.`;
};
